import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * api.ts — el contrato de Inicio.
 *
 * Los tipos están escritos a mano y eso es **deuda declarada, no una decisión**:
 * el backend declara un solo `response_model` en 27 routers, así que
 * `/openapi.json` no describe ninguna respuesta y no hay nada que generar. Están
 * copiados leyendo `content.py`, `events.py`, `celebrations.py`, `courses.py`,
 * `solicitudes.py` y la pantalla vanilla, campo por campo.
 *
 * ## Los endpoints NO cambian
 *
 * Son los mismos que ya pedía `page-dashboard.js`, con los mismos parámetros.
 * Dos cosas se ajustaron, y ninguna es un cambio de contrato:
 *
 *   · `/courses/` se pedía **dos veces** en la misma carga —`limit=100` para la
 *     inducción y `limit=50` para «Continúa aprendiendo»—. Ahora es UNA sola con
 *     `limit=100`. El endpoint ordena por `created_at DESC` (`courses.py:252`),
 *     así que los 100 son un superconjunto estable de los 50 en el mismo orden:
 *     el cálculo de «Continúa aprendiendo» no puede encontrar menos que antes.
 *
 *   · `/posts` bajó de `limit=15` a `limit=3`. Inicio ya no monta un muro —eso
 *     es Novedades—, sino tres titulares. Mismo endpoint, mismo parámetro.
 *
 * Y una **deja de pedirse**: `/users/me/profile`. Su único uso era la foto del
 * avatar del compositor, y el compositor se fue a Novedades. La foto del usuario
 * la sigue pintando el shell en la barra lateral, con la misma petición que ya
 * hacía `startApp()` — pedirla otra vez aquí era duplicarla.
 *
 * ## Lo que esta capa hace y la vanilla no: preguntar por el módulo ANTES
 *
 * Cuatro de las diez peticiones que lanzaba `page-dashboard.js` están detrás de
 * un `require_module` en el servidor, y ninguna se comprobaba antes de pedirla.
 * Para un rol sin ese módulo la respuesta era un 403 que el `catch` convertía en
 * «No se pudo cargar» —un mensaje que sugiere un fallo pasajero cuando lo que
 * pasa es que esa sección no está en su rol—.
 *
 * Cada hook de aquí recibe `habilitado` y el llamador se lo calcula con
 * `Auth.hasModule(...)`, que es lo mismo que ya hace el menú lateral con su
 * `data-module`. Ante la duda quien autoriza sigue siendo el servidor: esto solo
 * evita preguntar lo que ya se sabe que va a decir que no.
 */

/* ── Contadores ────────────────────────────────────────────────────────── */

export interface Estadisticas {
  active_courses?: number;
  completed_courses?: number;
  /** Recibidos, no enviados: desde que los reconocimientos los otorga solo
   *  Bienestar (`kudos:manage`), `kudos_given` es 0 permanente para todo
   *  colaborador. El servidor devuelve los dos; aquí se usa el que informa. */
  kudos_received?: number;
  kudos_given?: number;
  events_registered?: number;
}

/* ── Formación ─────────────────────────────────────────────────────────── */

export interface CursoDelCatalogo {
  id: number;
  title: string;
  is_mandatory?: boolean;
  enrolled?: boolean;
  completed?: boolean;
  progress_pct?: number;
}

/* ── Trámites ──────────────────────────────────────────────────────────── */

export interface ResumenDeTramites {
  /** Mías, sin cerrar. */
  abiertas: number;
  /** Mías que esperan algo de mí (devueltas para corregir, por ejemplo). */
  atencion: number;
  /** De otras personas, esperando mi aprobación. */
  por_aprobar: number;
}

/* ── Publicaciones ─────────────────────────────────────────────────────── */

export interface Publicacion {
  id: number;
  post_type: string;
  /** HTML enriquecido. Para los titulares se reduce a texto plano; ver
   *  `textoPlano()`. Nunca se inserta como marcado en esta pantalla. */
  content: string;
  author_name?: string;
  author_avatar?: string | null;
  created_at: string;
  reaction_count?: number;
  comment_count?: number;
  eliminada?: boolean;
}

/* ── Eventos ───────────────────────────────────────────────────────────── */

export interface EventoFijado {
  id: number;
  title: string;
  event_date: string;
  location?: string | null;
}

/* ── Claves de caché ───────────────────────────────────────────────────── */

export const claves = {
  estadisticas: ['inicio', 'estadisticas'] as const,
  cursos: ['inicio', 'cursos'] as const,
  tramites: ['inicio', 'tramites'] as const,
  publicaciones: ['inicio', 'publicaciones'] as const,
  fijados: ['inicio', 'fijados'] as const,
};

/* ── Los hooks ─────────────────────────────────────────────────────────── */

export function useEstadisticas() {
  return useQuery({
    queryKey: claves.estadisticas,
    queryFn: () => api.get<Estadisticas>('/stats/dashboard'),
  });
}

/** UNA sola llamada para la inducción y para «Continúa aprendiendo».
 *  `require_module("cursos")` en el servidor (`courses.py:237`). */
export function useCursos(habilitado: boolean) {
  return useQuery({
    queryKey: claves.cursos,
    queryFn: () => api.get<CursoDelCatalogo[]>('/courses/', { limit: 100 }),
    enabled: habilitado,
  });
}

/** Sin guard de módulo: `get_current_user` (`solicitudes.py:1509`). Solicitudes
 *  es `module: null` — la tienen todos los roles. */
export function useTramites() {
  return useQuery({
    queryKey: claves.tramites,
    queryFn: () => api.get<ResumenDeTramites>('/solicitudes/resumen'),
  });
}

/** `require_module("feed")` (`content.py:245`). */
export function useUltimasPublicaciones(habilitado: boolean) {
  return useQuery({
    queryKey: claves.publicaciones,
    queryFn: () => api.get<Publicacion[]>('/posts', { limit: 3 }),
    enabled: habilitado,
  });
}

/** Sin guard de módulo, **a propósito**: los fijados se ven en el Inicio de
 *  todo el mundo (`events.py:70`). */
export function useFijados() {
  return useQuery({
    queryKey: claves.fijados,
    queryFn: () => api.get<EventoFijado[]>('/events/pinned'),
  });
}

/* ── Ayudantes de presentación ─────────────────────────────────────────── */

/**
 * El texto de una publicación, sin marcado.
 *
 * `content` llega como HTML enriquecido (el compositor de Novedades permite
 * negrita, color y menciones). Un titular no necesita nada de eso, y meterlo en
 * el DOM como marcado abriría una superficie de escapado que esta pantalla no
 * tiene por qué tener.
 *
 * `DOMParser` no ejecuta scripts ni carga recursos —a diferencia de asignar a
 * `innerHTML`—, así que parsear aquí es seguro aunque el contenido venga sucio.
 * Lo que sale es `textContent`: texto y nada más.
 */
export function textoPlano(html: string): string {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
}

/** Los cursos obligatorios y cuántos van completos. `null` si no hay ninguno:
 *  una organización sin inducción no debería ver la tarjeta. */
export function progresoDeInduccion(cursos: CursoDelCatalogo[] | undefined) {
  if (!cursos) return null;
  const obligatorios = cursos.filter((c) => c.is_mandatory);
  if (!obligatorios.length) return null;
  const completo = (c: CursoDelCatalogo) => c.completed || (c.progress_pct ?? 0) >= 100;
  const hechos = obligatorios.filter(completo).length;
  return {
    hechos,
    total: obligatorios.length,
    pct: Math.round((hechos / obligatorios.length) * 100),
    siguiente: obligatorios.find((c) => !completo(c)) ?? null,
  };
}

/** El curso empezado y no terminado con más avance. `null` si no hay ninguno. */
export function cursoParaContinuar(cursos: CursoDelCatalogo[] | undefined) {
  if (!cursos) return null;
  return (
    cursos
      .filter((c) => c.enrolled && !c.completed && (c.progress_pct ?? 0) > 0)
      .sort((a, b) => (b.progress_pct ?? 0) - (a.progress_pct ?? 0))[0] ?? null
  );
}
